import React from "react";

/**
 * Vertical sidebar tab strip. Used inside content panels that have a
 * narrow vertical list on the left and content on the right —
 * specifically the HR sub-tabs (Employee management, HR reports,
 * Punch editor, Web forms) where each tab is a multi-page workflow.
 *
 * Counterpart to ResponsiveTabs (which is horizontal). Both follow the
 * same prop shape so the consumer can swap layouts cheaply.
 *
 * Pure presentation. Caller owns the activeKey state. The consumer
 * renders the active tab's content panel separately — this component
 * does NOT lazy-mount panels; it just emits the side rail.
 *
 * Pattern guidance:
 *   <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr',
 *                  gap: 16, alignItems: 'start' }}>
 *     <SideNavTabs tabs={...} activeKey={...} onChange={...} />
 *     <div className="dk-sidenav-panel">{currentContent}</div>
 *   </div>
 *
 * Props
 *  - tabs:        array of { key, label, icon?, hidden?, disabled? }
 *                 - icon can be a string (ri-* class) OR a ReactNode
 *                   (e.g. an inline SVG). String icons render via
 *                   <i className={...}> for kit consistency; ReactNode
 *                   icons render as-is (legacy callers used inline
 *                   <svg>s for finer control).
 *  - activeKey:   currently active tab key
 *  - onChange:    (key) => void
 *  - width:       sidebar width in px (default 200)
 *  - className:   extra className on the wrapper
 *  - ariaLabel:   accessible label for the tablist
 */
const TONE_PRIMARY = "#2b3f8c";       // brand blue
const TONE_PRIMARY_BG = "#eef1fa";    // soft blue background for active
const TONE_PRIMARY_TEXT = "#1a2a6b";  // dark blue text for active
const TONE_TEXT = "#6b7280";          // muted body text
const TONE_HINT = "#9ca3af";          // hint / inactive icon
const TONE_BORDER = "#e2e2e2";        // soft border
const TONE_WHITE = "#fff";

const renderIcon = (icon, isActive) => {
  if (!icon) return null;
  // String icon class (ri-*, mdi-*, etc.) — render via <i>
  if (typeof icon === "string") {
    return (
      <i
        className={icon}
        style={{
          fontSize: 16,
          color: isActive ? TONE_PRIMARY : TONE_HINT,
          flexShrink: 0,
          width: 16,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        aria-hidden="true"
      />
    );
  }
  // ReactNode — assume the consumer wants full control of the rendered
  // element. Wrap in a color-inheriting span so the active/inactive
  // color flows down via currentColor (which inline SVGs use for stroke).
  return (
    <span
      style={{
        color: isActive ? TONE_PRIMARY : TONE_HINT,
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
      }}
      aria-hidden="true"
    >
      {icon}
    </span>
  );
};

const SideNavTabs = ({
  tabs = [],
  activeKey,
  onChange,
  width = 200,
  className = "",
  ariaLabel = "Section navigation",
}) => {
  const visible = tabs.filter((t) => !t.hidden);

  return (
    <div
      role="tablist"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      className={`dk-sidenav ${className}`}
      style={{
        background: TONE_WHITE,
        border: `0.5px solid ${TONE_BORDER}`,
        borderRadius: 12,
        overflow: "hidden",
        width,
        flexShrink: 0,
      }}
    >
      {visible.map((tab) => {
        const isActive = tab.key === activeKey;
        const isDisabled = !!tab.disabled;
        return (
          <div
            key={tab.key}
            role="tab"
            tabIndex={isDisabled ? -1 : 0}
            aria-selected={isActive}
            aria-disabled={isDisabled}
            onClick={() => {
              if (isDisabled) return;
              if (typeof onChange === "function" && tab.key !== activeKey) {
                onChange(tab.key);
              }
            }}
            onKeyDown={(e) => {
              if (isDisabled) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                if (typeof onChange === "function" && tab.key !== activeKey) {
                  onChange(tab.key);
                }
              }
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "11px 16px",
              fontSize: 13,
              cursor: isDisabled ? "not-allowed" : "pointer",
              color: isActive ? TONE_PRIMARY_TEXT : TONE_TEXT,
              fontWeight: isActive ? 500 : 400,
              borderLeft: isActive
                ? `3px solid ${TONE_PRIMARY}`
                : "3px solid transparent",
              background: isActive ? TONE_PRIMARY_BG : TONE_WHITE,
              transition: "all .15s",
              userSelect: "none",
              opacity: isDisabled ? 0.5 : 1,
            }}
          >
            {renderIcon(tab.icon, isActive)}
            <span style={{ minWidth: 0, flex: 1 }}>{tab.label}</span>
          </div>
        );
      })}
    </div>
  );
};

export default SideNavTabs;
